import React, { useEffect, useMemo, useState } from 'react';
import './DeviceInventory.less';
import {
  AddPlusIcon,
  CloseIcon,
  DeviceInventoryIcon,
  DeviceUnpairIcon,
  ErrorIcon,
  HC200Icon,
  NewUpdateIcon,
  NewCircleIcon,
} from '../Common/Icons';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { Access, checkRoleAccess } from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import DeviceInventoryList from './DeviceInventoryList';
import DeviceInventoryModal from './DeviceInventoryModal';
import { Button, Input, message, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);
import { devicesInventory } from '../../utilities/data';
import useConfig from '../Hooks/useConfig';
import LocalStorage from '@vantiva-sa/homesight_humaui/Utilities/LocalStorage';
import { AppStorageKeys, UserRoles } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { get } from 'lodash';
import { CONFIG_BIOMETRICS_ENABLE_KEY, CONFIG_SMARTHOME_ENABLE_KEY } from '@/constants/device';
import { useAssignSmartCameraHandler } from './useAssignSmartCameraHandler';
import AssignSmartCameraModal from './AssignSmartCameraModal';
import { useAddRadarSensorHandler } from './useAddRadarSensorHandler';
import AddRadarSensorModal from './AddRadarSensorModal';
import {
  useDisassociateDeviceMutation,
  useGetPontosenseTenantIdQuery,
  useGetSensorDetailsMutation,
} from '@/api';
import {
  getPontosenseAssociationIds,
  isPontosenseRadarSensor,
} from '@/utilities/helpers/pontosenseHelper';

const { device_types } = devicesInventory;

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getDeviceInfo = (device, trans) => {
  const isUnpaired = device?.paired_status === 'UNPAIRED';
  if (device_types?.sensor_devices?.models?.includes(device?.modelnumber)) {
    return [
      {
        key: trans('LOCATIONNAME'),
        value: device?.device_location || '-',
      },
      {
        key: trans('SERIALNUMBER'),
        value: device?.device_serial_number || '-',
      },
      { key: trans('HWVERSION'), value: device?.device_hardware_version || '-' },
      { key: trans('SWVERSION'), value: device?.device_software_version || '-' },
      {
        key: trans('SIGNALLEVEL'),
        value:
          !isUnpaired && device?.device_signal_strength
            ? `${device.device_signal_strength} ${trans('DBM')}`
            : '-',
      },
    ];
  }
  if (device_types?.smart_camera_devices?.models?.includes(device?.modelnumber)) {
    return [
      {
        key: trans('SOFTWAREVERSION'),
        value: device?.softwareversion || '-',
      },
      {
        key: trans('MODEL'),
        value: device?.modelnumber || '-',
      },
    ];
  }
  if (device_types?.remote_control_devices?.models?.includes(device?.modelnumber)) {
    return [
      { key: trans('MODEL'), value: device?.device_name || '-' },
      { key: trans('SWVERSION'), value: device?.device_software_version || '-' },
      {
        key: trans('SIGNALLEVEL_L'),
        value: (!isUnpaired && device?.device_signal_level) || '-',
      },
      { key: trans('MACADDRESS'), value: device?.device_mac_address || '-' },
    ];
  }

  if (
    device_types?.medical_devices?.models?.includes(device?.devicetype) ||
    (device?.devicetype &&
      ![
        ...device_types?.sensor_devices?.models,
        ...device_types?.smart_camera_devices?.models,
        ...device_types?.remote_control_devices?.models,
      ].includes(device?.modelnumber))
  ) {
    return [
      {
        key: trans('MANUFACTURER'),
        value: device?.device_manufacturer || '-',
      },
      {
        key: trans('MODEL'),
        value: device?.modelname || '-',
      },
      { key: trans('SERIALNUMBER'), value: device?.device_serial_number || '-' },
      { key: trans('MAC'), value: device?.device_mac_address || '-' },
      { key: trans('HWVERSION_L'), value: device?.device_hardware_revision || '-' },
      { key: trans('SWVERSION_L'), value: device?.device_software_version || '-' },
    ];
  }

  return [];
};

const getDeviceCategory = (device) => {
  if (
    device_types?.smart_camera_devices?.models?.includes(device?.modelnumber) ||
    device_types?.remote_control_devices?.models?.includes(device?.modelnumber)
  ) {
    return 'second_screen';
  }
  if (
    device_types?.medical_devices?.models?.includes(device?.devicetype) ||
    device?.productclass === 'wellness'
  ) {
    return 'biometrics';
  }
  if (
    device?.productclass === 'smart_home' ||
    device?.productclass === 'environmental_sensor' ||
    device_types?.sensor_devices?.models?.includes(device?.modelnumber)
  ) {
    return 'smart_home';
  }
  return 'other';
};

const mapEventData = (device) => {
  if (device_types?.remote_control_devices?.models?.includes(device?.device_name)) {
    return {
      connectionStatus: device?.device_connection_status ? 'CONNECTED' : 'DISCONNECTED',
    };
  } else {
    return {
      connectionStatus: device?.device_connected ? 'CONNECTED' : 'DISCONNECTED',
    };
  }
};

const removeDevice = (
  userApi,
  hubRecord,
  device,
  trans,
  fetchDevices,
  refetch,
  setHeaderRefreshKey,
  queryParams,
  setDeviceLocation,
  setDeviceStatus,
  setStatusTitle,
  setHasDevice
) => {
  const errorCallback = (error) => {
    console.error('Error removing device:', error);
    // message.error("Error removing device" + ": " + error.message);
  };

  const successCallback = (data) => {
    // message.success("Device removed successfully");
    refetch();
    setHasDevice(false);
    fetchDevices(queryParams, (fetchedDevices) => {
      if (!fetchedDevices.find((d) => d.deviceuuid === hubRecord?.deviceuuid)) {
        setDeviceStatus(''); // Clear device status
        setDeviceLocation(null); // Clear device location
        setStatusTitle(''); // Clear status title
        setHeaderRefreshKey('remove_device');
      }
    });
  };

  try {
    // Prepare the request payload
    const userDisAssociation = {
      deviceuuid: device.deviceuuid,
    };

    // Make the API call
    userApi.disassociateDeviceFromUser(
      hubRecord.useruuid,
      userDisAssociation,
      apiCallback({
        translator: trans,
        failCallback: errorCallback,
        errorCallback,
        successCallback,
      })
    );
  } catch (error) {
    errorCallback(error);
  }
};

const actionsData = (device, trans) => {
  const UnpairDescription = () => (
    <div>
      <div>
        <span className="custom-hint-label">{trans('THEDEVIECWILLBEUNPAIRED')}</span>
        {`${trans('DATAFROMDEVICEREMAINASSOCIATEDWITHACCOUNTUNTILCLEARED')}`}
      </div>
      <div className="unpair-action-info-container">
        <span>
          <ErrorIcon fillColor="#808080" />
        </span>
        <div>
          <span className="unpair-custom-information-header">{`${trans('INFORMATION')}:`}</span>
          <span className="unpair-custom-information-label">
            {`${trans('UNPAIREDLABELINFORMATION')}`}
          </span>
        </div>
      </div>
    </div>
  );
  if (device_types?.smart_camera_devices?.models?.includes(device?.modelnumber)) {
    return {
      restart_hc200: {
        title: trans('RESTART'),
        description: (
          <div>
            <span className="custom-hint-label">{trans('HINT:')} </span>
            {trans('HC200WILLIMMEDIATELYRESTART')}
          </div>
        ),
        action: 'device_reboot',
      },
      factory_reset_hc200: {
        title: trans('FACTORYRESET'),
        description: (
          <div>
            <span className="custom-hint-label">{trans('HINT:')} </span>
            {trans('HC200WILLIMMEDIATELYCLEARALLUSERDATAANDRESTOREFACTORYDEFAULTRESTART')}
            <span style={{ fontSize: '12px' }}>
              <br /> {trans('WARNINGEM200PAIREDHC200USEUNPAIREM200ACTIONFIRST')}
              <br />
              {trans('WARNINGEM200PAIREDHC200USEUNPAIREM200ACTIONFIRSTDESCRIPTION')}
            </span>
          </div>
        ),
        action: 'factory_reset',
      },
      check_for_updates_hc200: {
        title: trans('CHECKFORUPDATES'),
        description: (
          <div>
            <span className="custom-hint-label">{trans('HINT:')} </span>
            {trans('HC200WILLCHECKFORANYUPDATESANDIMMEDIATELYDOWNLOADANDINSTALL')}
          </div>
        ),
        action: 'perform_checkforupdates',
      },
      remove_device: {
        title: trans('REMOVEDEVICE'),
        description: (
          <div>
            <span className="custom-hint-label">{trans('HINT:')} </span>
            {trans('THISWILLREMOVEDEVICEFROMUSER')}
          </div>
        ),
      },
    };
  }

  if (device_types?.sensor_devices?.models?.includes(device?.modelnumber)) {
    return {
      unpair_em200: {
        title: trans('UNPAIR'),
        description: <UnpairDescription />,
        action: 'perform_unPairAccessories',
        message: trans('SIGNALSENTTODEVICETOUNPAIREM200'),
      },
    };
  }

  if (device_types?.remote_control_devices?.models?.includes(device?.modelnumber)) {
    return {
      unpair_rcu: {
        title: trans('UNPAIR'),
        description: <UnpairDescription />,
        action: 'perform_unPairRCU',
        message: trans('SIGNALSENTTODEVICETOUNPAIRRCU'),
      },
      find_me: {
        title: trans('FINDME'),
        description: (
          <div>
            <span className="custom-hint-label">{trans('HINT:')} </span>
            {trans('SUBSCRIBERREMOTECONTROLEDESCRIPTION')}
          </div>
        ),
        action: 'perform_findRCU',
        message: trans('FINDREMOTEALERTSENT'),
      },
    };
  }

  // Check if device.devicetype is in medicalDeviceTypes
  if (
    device_types?.medical_devices?.models?.includes(device?.devicetype) ||
    (device?.devicetype &&
      ![
        ...device_types?.sensor_devices?.models,
        ...device_types?.smart_camera_devices?.models,
        ...device_types?.remote_control_devices?.models,
      ].includes(device?.modelnumber))
  ) {
    return {
      unpair_medical_device: {
        title: trans('UNPAIR'),
        description: <UnpairDescription />,
        action: isPontosenseRadarSensor(device)
          ? 'pontosense_disassociate'
          : 'perform_unPairAccessories',
        ...getPontosenseAssociationIds(device),
        message: trans('SIGNALSENTTODEVICETOUNPAIRMEDICALDEVICE'),
      },
    };
  }

  return {};
};

const performAction = (deviceManagementApi, data, providerid, trans, params) => {
  const errorCallback = (error) => {
    console.error(error);
    // message.error("Error performing: " + data.title + " " + error.message);
  };
  const successCallback = (info) => {
    if (data.message) {
      // message.success(data.message);
      console.warn(data.message);
    } else {
      // message.success("Signal sent to Device for: " + data.title);
      console.warn('Signal sent to Device for: ' + data.title);
    }
  };

  try {
    deviceManagementApi.performCommand(
      data.action,
      providerid,
      data.deviceUuid,
      params ? { source: 'System', ...params } : { source: 'System' },
      apiCallback({
        translator: trans,
        failCallback: errorCallback,
        errorCallback,
        successCallback,
      })
    );
  } catch (error) {
    errorCallback(error);
  }
};

const DeviceInventory = ({
  hubRecord,
  timezone,
  deviceStatus,
  providerid,
  loginUserTimeformat,
  fetchDevices,
  setHeaderRefreshKey,
  queryParams,
  setStatusTitle,
  setDeviceStatus,
  setDeviceLocation,
  hasDevice,
  setHasDevice,
  hubConfig: hubConfigProp,
  hubUserRole: hubUserRoleProp,
}) => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [filteredAccessoriesData, setFilteredAccessoriesData] = useState([]);
  const {
    isSmartCameraModalOpen,
    setIsSmartCameraModalOpen,
    readyToAssignDevices,
    searchText,
    setSearchText,
    searchApplied,
    selectedSmartCameraDevice,
    readyToAssignloading,
    handleDeviceSmartCameraSelect,
    handleSearchApply,
    handleClearSearch,
    handleAssignSmartCamera,
    closeModal,
    assignDevice,
    refetch,
    dataFetched,
    accessoriesData,
    setSearchApplied,
  } = useAssignSmartCameraHandler(
    fetchDevices,
    hubRecord,
    setDeviceStatus,
    setFilteredAccessoriesData,
    setSelectedDevice
  );

  const {
    isAddRadarSensorModalOpen,
    openAddRadarSensorModal,
    closeAddRadarSensorModal,
    homeId,
    setHomeId,
    submitAddRadarSensor,
    isSubmitting: isSubmittingRadarSensor,
  } = useAddRadarSensorHandler({ hubRecord, providerid, refetch, fetchDevices, queryParams });

  const allowAddDevice = useMemo(() => checkRoleAccess(Access.beta.labsPreview), []);

  const trans = useSelector(translator);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const userApi = useSelector((state) => state.apis.userApi);
  const deviceManagementApi = useSelector((state) => state.apis.deviceManagementApi);
  const { data: pontosenseTenantId } = useGetPontosenseTenantIdQuery(providerid);
  const disassociatePontosenseMutation = useDisassociateDeviceMutation({
    onSuccess: () => {
      message.success(trans('RADARSENSORUNPAIREDSUCCESSFULLY'));
      refetch?.();
      fetchDevices?.(queryParams);
      setIsModalVisible(false);
      setSelectedDevice(null);
    },
    onError: () => {
      message.error(trans('FAILEDTOUNPAIRRADARSENSOR'));
    },
  });
  const sensorDetailsMutation = useGetSensorDetailsMutation({
    onSuccess: (sensorDetails) => {
      const homeId = sensorDetails?.pontosense_home_id;
      if (!homeId) {
        message.error(trans('FAILEDTOUNPAIRRADARSENSOR'));
        return;
      }
      disassociatePontosenseMutation.mutate({ tenantId: pontosenseTenantId, homeId });
    },
    onError: () => {
      message.error(trans('FAILEDTOUNPAIRRADARSENSOR'));
    },
  });
  const { data: userConfig = {} } = useConfig({ useruuid: hubRecord?.useruuid });
  const hubConfig = hubConfigProp || userConfig;
  const hubUserRole = hubUserRoleProp || hubRecord?.rolename;

  const isSmartHomeEnabled = useMemo(
    () => get(hubConfig, CONFIG_SMARTHOME_ENABLE_KEY, true),
    [hubConfig]
  );
  const isBiometricsEnabled = useMemo(
    () => get(hubConfig, CONFIG_BIOMETRICS_ENABLE_KEY, true),
    [hubConfig]
  );

  const hasSmartHomeAccess = useMemo(() => {
    if (!isSmartHomeEnabled) return false;
    if (hubUserRole && ![UserRoles.manage, UserRoles.care].includes(hubUserRole)) {
      return false;
    }
    return true;
  }, [isSmartHomeEnabled, hubUserRole]);

  const hasBiometricsAccess = useMemo(() => {
    if (!isBiometricsEnabled) return false;
    if (hubUserRole && ![UserRoles.manage, UserRoles.care].includes(hubUserRole)) {
      return false;
    }
    return true;
  }, [isBiometricsEnabled, hubUserRole]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const deviceTabs = useMemo(
    () => [
      { key: 'all', label: trans('ALL') },
      ...(hasSmartHomeAccess ? [{ key: 'smart_home', label: trans('SMARTHOME') }] : []),
      ...(hasBiometricsAccess ? [{ key: 'biometrics', label: trans('BIOMETRICS') }] : []),
      // { key: 'second_screen', label: trans('SECONDSCREEN') },
    ],
    [trans, hasSmartHomeAccess, hasBiometricsAccess]
  );

  const currentActiveTab = useMemo(() => {
    if (activeTab === 'smart_home' && !hasSmartHomeAccess) return 'all';
    if (activeTab === 'biometrics' && !hasBiometricsAccess) return 'all';
    return activeTab;
  }, [activeTab, hasSmartHomeAccess, hasBiometricsAccess]);

  const tabFilteredData = useMemo(
    () =>
      currentActiveTab === 'all'
        ? filteredAccessoriesData
        : filteredAccessoriesData.filter(
            (device) => getDeviceCategory(device) === currentActiveTab
          ),
    [filteredAccessoriesData, currentActiveTab]
  );

  const emptyStateContent = useMemo(() => {
    const emptyStateByTab = {
      all: {
        title: trans('ADDSOMEDEVICES'),
        description: trans('ONCEYOUHAVEADDEDDEVICESITWILLBEFOUNDHERE'),
      },
      smart_home: {
        title: trans('NOSMARTHOMEDEVICESFOUND'),
        description: trans('ADDSMARTHOMEDEVICESTOSEEITSHERE'),
      },
      biometrics: {
        title: trans('NOBIOMETRICSDEVICESFOUND'),
        description: trans('ADDBIOMETRICSDEVICESTOSEEITSHERE'),
      },
    };

    return emptyStateByTab[currentActiveTab] || emptyStateByTab.all;
  }, [currentActiveTab, trans]);

  const videoCallNotificationModalVisible = useSelector(
    (state) => state.notification.videoCallNotificationModalVisible
  );

  useEffect(() => {
    if (videoCallNotificationModalVisible) {
      handleModalClose();
    }
  }, [videoCallNotificationModalVisible]);

  useEffect(() => {
    if (accessoriesData.length) {
      const data = accessoriesData
        .filter((device) => !device_types?.tv_devices?.models?.includes(device?.modelnumber))
        .sort((a, b) => {
          // Define primary order for each main category (Smart Camera, Remote, Sensors, Medical)
          const getDeviceOrder = (device) => {
            if (device_types?.smart_camera_devices?.models?.includes(device.modelnumber)) return 1; // HC200
            if (
              device_types?.remote_control_devices?.models?.includes(device.modelnumber) &&
              device.paired_status === 'PAIRED'
            )
              return 2; // Remote Control
            if (device.productclass === 'smart_home' && device.paired_status === 'PAIRED') return 3; // Smart Home
            if (device.productclass === 'environmental_sensor' && device.paired_status === 'PAIRED')
              return 3; // Environmental Sensor

            if (device.productclass === 'wellness' && device.paired_status === 'PAIRED') return 4; // Medical devices

            if (
              device_types?.remote_control_devices?.models?.includes(device.modelnumber) &&
              device.paired_status === 'UNPAIRED'
            )
              return 5; // Remote Control

            if (device.productclass === 'smart_home' && device.paired_status === 'UNPAIRED')
              return 6; // Smart Home
            if (
              device.productclass === 'environmental_sensor' &&
              device.paired_status === 'UNPAIRED'
            )
              return 6; // Environmental Sensor

            if (device.productclass === 'wellness' && device.paired_status === 'UNPAIRED') return 7; // Medical devices
            return 8; // Fallback for any other devices
          };
          // Define secondary order: Paired devices first, then unpaired
          const pairedOrder = (device) => (device.paired_status === 'PAIRED' ? 0 : 1);

          // Define tertiary order for alphabetical sorting
          const alphabeticalOrder = (device) => {
            if (getDeviceOrder(device) === 3) {
              // For smart home devices, use device_location for alphabetical order
              return device.location?.toLowerCase() || '';
            }
            return device.device_location?.toLowerCase();
          };

          // Compare devices based on all three sorting conditions
          const orderA = [getDeviceOrder(a), pairedOrder(a), alphabeticalOrder(a)];
          const orderB = [getDeviceOrder(b), pairedOrder(b), alphabeticalOrder(b)];
          // Apply the sorting logic in sequence
          for (let i = 0; i < orderA.length; i++) {
            if (orderA[i] < orderB[i]) return -1;
            if (orderA[i] > orderB[i]) return 1;
          }
          return 0;
        });
      setFilteredAccessoriesData(data);
    }
  }, [accessoriesData]);

  const showModal = (device) => {
    setSelectedDevice(device);
    setIsModalVisible(true);
    handleClearSearch();
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedDevice(null);
  };

  return (
    <div style={{ padding: '40px 40px 0px 40px' }}>
      {/* {!hasDevice && (
        <div className="device-page-no-devices-banner">
          <div className="device-page-banner-container-1">
            <div>
              <DeviceUnpairIcon
                className="unpair-icon"
                strokeColor="#FFA500"
                fillColor="#FFA500"
              />
            </div>
            <span className="device-banner-text">
              <span className="device-banner-bold">
                {`The account is almost fully setup: `}
              </span>
              {` please review`}
            </span>
          </div>
          <div className="device-page-banner-container-1">
            <label className="radio-container" style={{ visibility: "hidden" }}>
              <NewCircleIcon />
              <span className="radio-label">Review Account Profile</span>
            </label>
            <label className="radio-container">
              {hasDevice ? (
                <NewUpdateIcon className="completed-selected-icon" />
              ) : (
                <NewCircleIcon />
              )}
              <span
                className={`radio-label ${
                  hasDevice ? "assigned-radio-label" : ""
                }`}
              >
                Assign Smart Camera
              </span>
            </label>
            <label className="radio-container" style={{ visibility: "hidden" }}>
              <NewCircleIcon />
              <span className="radio-label">Assign Staff</span>
            </label>
          </div>
        </div>
      )} */}
      <div className="device-header-container">
        <div className="device-header-icon-title">
          <div className="device-header-icon">
            <DeviceInventoryIcon style={{ fill: '#113E57', width: '29px', height: '32px' }} />
          </div>
          <div className="device-header-title">{trans('DEVICES')}</div>
        </div>
        <div className="device-header-actions">
          {dataFetched && filteredAccessoriesData.length === 0 && (
            <div className="device-page-assign-camera-container" onClick={handleAssignSmartCamera}>
              <AddPlusIcon className="smart-camera-add-icon" />
              <span className="assign-smart-camera-title">{trans('ASSIGNCAMERA')}</span>
            </div>
          )}
          {allowAddDevice && (
            <div className="device-page-add-device-container" onClick={openAddRadarSensorModal}>
              <AddPlusIcon className="smart-camera-add-icon add-device-icon" />
              <span className="assign-smart-camera-title add-device-title">
                {trans('ADDDEVICE')}
              </span>
            </div>
          )}
        </div>
      </div>
      {dataFetched && filteredAccessoriesData.length > 0 && (
        <div className="device-tabs-container">
          {deviceTabs.map((tab) => (
            <div
              key={tab.key}
              className={`device-tab ${currentActiveTab === tab.key ? 'device-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.key === 'all' ? `${tab.label} (${filteredAccessoriesData.length})` : tab.label}
            </div>
          ))}
        </div>
      )}
      <Spin spinning={!dataFetched} style={{ margin: '70px -20px' }}>
        {dataFetched && (
          <>
            <div style={{ padding: '16px' }}>
              {tabFilteredData.length === 0 ? (
                <div className="no-devices-container">
                  <div className="no-devices-card">
                    <div className="no-devices-info">
                      <div className="label">{emptyStateContent.title}</div>
                      <div className="description">{emptyStateContent.description}</div>
                    </div>
                  </div>
                </div>
              ) : (
                tabFilteredData.map((device, index) => {
                  const isExpanded = device?.modelnumber === 'HC200';
                  const key = device?.deviceId || index;

                  return (
                    <DeviceInventoryList
                      key={key}
                      device={device}
                      index={index}
                      isExpanded={isExpanded}
                      showModal={showModal}
                      deviceStatus={deviceStatus}
                      getDeviceInfo={(device) => getDeviceInfo(device, trans)}
                      timezone={timezone ? timezone?.split(' ')[0] : LOCAL_TIMEZONE}
                      connectionStatus={mapEventData(device).connectionStatus}
                      loginUserTimeformat={loginUserTimeformat}
                      userConfig={hubConfig}
                      selectedLanguage={LocalStorage.get(AppStorageKeys.language) || 'en'}
                    />
                  );
                })
              )}
            </div>
          </>
        )}
      </Spin>
      {isModalVisible && (
        <DeviceInventoryModal
          isModalVisible={isModalVisible}
          handleOk={handleModalClose}
          handleCancel={handleModalClose}
          device={selectedDevice}
          selectedDeviceInfo={getDeviceInfo(selectedDevice, trans)}
          deviceStatus={deviceStatus}
          timezone={timezone ? timezone?.split(' ')[0] : LOCAL_TIMEZONE}
          connectionStatus={mapEventData(selectedDevice).connectionStatus}
          hubRecord={hubRecord}
          providerid={providerid}
          performAction={(data, params) => {
            if (data?.action === 'pontosense_disassociate') {
              sensorDetailsMutation.mutate({
                tenantId: pontosenseTenantId,
                sn: selectedDevice?.deviceid,
              });
              return;
            }
            performAction(deviceManagementApi, data, providerid, trans, params);
          }}
          actionsData={actionsData(selectedDevice, trans)}
          loginUserTimeformat={loginUserTimeformat}
          accessoriesData={accessoriesData}
          userConfig={hubConfig}
          removeDevice={(device) => {
            removeDevice(
              userApi,
              hubRecord,
              device,
              trans,
              fetchDevices,
              refetch,
              setHeaderRefreshKey,
              queryParams,
              setDeviceLocation,
              setDeviceStatus,
              setStatusTitle,
              setHasDevice
            );
          }}
          selectedLanguage={LocalStorage.get(AppStorageKeys.language) || 'en'}
          setIsSmartCameraModalOpen={setIsSmartCameraModalOpen}
        />
      )}

      {isSmartCameraModalOpen && (
        <AssignSmartCameraModal
          isSmartCameraModalOpen={isSmartCameraModalOpen}
          closeModal={closeModal}
          searchText={searchText}
          setSearchText={setSearchText}
          searchApplied={searchApplied}
          handleSearchApply={handleSearchApply}
          handleClearSearch={handleClearSearch}
          readyToAssignDevices={readyToAssignDevices}
          readyToAssignloading={readyToAssignloading}
          selectedSmartCameraDevice={selectedSmartCameraDevice}
          handleDeviceSmartCameraSelect={handleDeviceSmartCameraSelect}
          assignDevice={assignDevice}
          hubRecord={hubRecord}
          fetchDevices={fetchDevices}
          setHasDevice={setHasDevice}
          queryParams={queryParams}
          setDeviceLocation={setDeviceLocation}
          setDeviceStatus={setDeviceStatus}
          setStatusTitle={setStatusTitle}
          setHeaderRefreshKey={setHeaderRefreshKey}
          refetch={refetch}
        />
      )}

      {isAddRadarSensorModalOpen && (
        <AddRadarSensorModal
          isAddRadarSensorModalOpen={isAddRadarSensorModalOpen}
          closeAddRadarSensorModal={closeAddRadarSensorModal}
          homeId={homeId}
          setHomeId={setHomeId}
          submitAddRadarSensor={submitAddRadarSensor}
          isSubmitting={isSubmittingRadarSensor}
        />
      )}
    </div>
  );
};

export default DeviceInventory;
